import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Full-screen blocking screen shown whenever the signed-in user still has a
// temporary password (must_change_password=true). It is the ONLY thing the user
// can interact with (besides Sign out) until they set a new password.
export default function ForcePasswordChange() {
  const { user, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const landing = user?.role === "supplier" ? "/supplier" : "/";

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("New password and confirmation do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      // Full reload so the cleared flag + fresh permissions are loaded cleanly,
      // then land on the role-appropriate page.
      window.location.assign(landing);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password change failed");
      setBusy(false);
    }
  };

  return (
    <div data-testid="force-pw-screen" className="min-h-screen w-full flex items-center justify-center bg-[#050505] text-white px-4">
      <div className="w-full max-w-md border border-white/10 bg-[#0b0b0b] p-8">
        <div className="text-[#FFB300] font-mono-data text-lg tracking-[0.3em] mb-1">+++</div>
        <h1 className="text-2xl font-semibold tracking-tight">Set a new password</h1>
        <p data-testid="force-pw-reason" className="text-zinc-500 text-sm mt-2 mb-6">
          Your account is using a temporary password. For security, you must set a new
          password before you can access the workspace.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Current (temporary) password" testid="force-pw-current"
                 value={current} onChange={setCurrent} />
          <Field label="New password (min 8)" testid="force-pw-new"
                 value={next} onChange={setNext} />
          <Field label="Confirm new password" testid="force-pw-confirm"
                 value={confirm} onChange={setConfirm} />
          <button
            type="submit" disabled={busy} data-testid="force-pw-submit"
            className="w-full bg-[#FFB300] text-black font-semibold text-sm py-2.5 tracking-wide hover:bg-[#ffc333] transition-colors disabled:opacity-60"
          >
            {busy ? "Updating…" : "Update password & continue"}
          </button>
        </form>
        <button
          onClick={logout} data-testid="force-pw-logout"
          className="mt-4 w-full text-zinc-500 hover:text-white text-xs tracking-widest uppercase transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange }) {
  return (
    <div>
      <div className="text-zinc-500 text-[10px] uppercase tracking-widest mb-1">{label}</div>
      <input
        type="password" data-testid={testid} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black border border-white/15 text-zinc-100 text-sm px-3 py-2 focus:border-[#FFB300] outline-none"
        autoComplete="off"
      />
    </div>
  );
}
